import { APP_BASE_HREF, IMAGE_LOADER, ImageLoaderConfig, PlatformLocation } from '@angular/common';
import {
  APP_ID,
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  inject,
  isDevMode,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  provideRouter,
  TitleStrategy,
  withComponentInputBinding,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig,
  withViewTransitions,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideFirebaseAnalytics } from '@bhindle/shared/analytics';
import {
  APP_VERSION,
  CDN_ORIGIN,
  provideCdnOrigin,
  TitleStrategyService,
  withChunkReloadRecovery,
} from '@bhindle/shared/core';
import { EMOJI_PATH } from '@bhindle/shared/emoji';
import {
  provideFirebaseApp,
  provideFirebaseAppCheck,
  provideFirebaseFunctions,
} from '@bhindle/shared/firebase';
import { ThemeService } from '@bhindle/shared/ui/theme';
import { initializeApp, setLogLevel } from 'firebase/app';
import { environment } from '../environments/environment';
import { OptOutPreloadStrategy } from './preload.strategy';
import { routes } from './shell.routes';

const firebaseApp = initializeApp(environment.fb, {
  // Must stay false. This flag opts the SDK into collection on its own, so with
  // it on the consent gate in `provideFirebaseAnalytics` decided nothing —
  // declining still collected.
  automaticDataCollectionEnabled: false,
  name: 'TaDone',
});

/** Deepest `data.level` of a route tree; used to derive navigation direction. */
const routeLevel = (snapshot: ActivatedRouteSnapshot): number => {
  let level = 0;
  let route: ActivatedRouteSnapshot | null = snapshot;
  while (route) {
    if (typeof route.data['level'] === 'number') level = route.data['level'];
    route = route.firstChild;
  }
  return level;
};
const analyticsProviders = await provideFirebaseAnalytics(firebaseApp);

if (!environment.production) {
  setLogLevel('verbose');
}
export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: APP_ID,
      useValue: 'TaDone',
    },
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      /* withDebugTracing(), */
      withComponentInputBinding(),
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
      }),
      withViewTransitions({
        onViewTransitionCreated: ({ transition, from, to }) => {
          const direction = routeLevel(to) - routeLevel(from);
          if (direction === 0) return;
          const cls = direction > 0 ? 'bd-nav-forward' : 'bd-nav-back';
          document.documentElement.classList.add(cls);
          transition.finished.finally(() => document.documentElement.classList.remove(cls));
        },
      }),
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'top',
      }),
      withChunkReloadRecovery(),
      withPreloading(OptOutPreloadStrategy),
    ),
    provideFirebaseApp(firebaseApp),
    provideFirebaseFunctions({
      region: 'europe-west1',
      emulator: environment.useEmulators ? { host: 'localhost', port: 5001 } : undefined,
    }),
    provideFirebaseAppCheck({
      reCaptchaSiteKey: environment.fb.appCheck,
      autoRefresh: !environment.useEmulators,
    }),
    analyticsProviders,
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerImmediately',
    }),
    {
      provide: APP_VERSION,
      useValue: environment.version,
    },
    {
      provide: APP_BASE_HREF,
      useFactory: (s: PlatformLocation) => s.getBaseHrefFromDOM(),
      deps: [PlatformLocation],
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'EUR',
    },
    ...provideCdnOrigin(environment.cdn),
    {
      provide: IMAGE_LOADER,
      useFactory: () => {
        const origin = inject(CDN_ORIGIN);
        // Any absolute URL passes through, not just https://. The origin is now
        // http://localhost:4502 locally, so narrowing this to https:// would
        // turn an http or protocol-relative photoURL into
        // "http://localhost:4502http://…". Members' avatars come from arbitrary
        // provider URLs, so this is reachable input, not a hypothetical.
        const isAbsolute = /^[a-z][a-z0-9+.-]*:|^\/\//i;
        return (config: ImageLoaderConfig) =>
          isAbsolute.test(config.src) ? config.src : `${origin}${config.src}`;
      },
    },
    {
      provide: EMOJI_PATH,
      useValue: '/emojis/emoji_u',
    },
    ThemeService,
    {
      provide: TitleStrategy,
      useClass: TitleStrategyService,
    },
  ],
};
